import React, { useEffect, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { useParams } from 'react-router-dom';
import {
  Box,
  Flex,
  Grid,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
} from '@chakra-ui/react';
import { getMerchantCompanyDetails } from 'api/internaltools';
import { useAuthentication } from 'AuthContext';
import { PermissionsV2 } from 'constants/permissions_v2';
import ChangeMerchantNameModal from 'modules/merchantinfo/components/ChangeMerchantNameModal';
import InsertTransactionModal from 'modules/merchantinfo/components/InsertTransactionModal';
import KycDocumentsModal from 'modules/merchantinfo/components/KycDocumentsModal';
import SubmitKycDocumentModal from 'modules/merchantinfo/components/SubmitKycDocumentModal';
import { TRANSACTIONS_TITLE } from 'modules/merchantinfo/constants/constants';
import MerchantAccountsDetails from 'modules/merchantinfo/MerchantAccountsDetails';
import MerchantAndEntityDetails from 'modules/merchantinfo/MerchantAndEntityDetails';
import MerchantBankingDetails from 'modules/merchantinfo/MerchantBankingDetails';
import MerchantCorridorsManagement from 'modules/merchantinfo/MerchantCorridorsManagement';
import MerchantEntitiesKYBDetails from 'modules/merchantinfo/MerchantEntitiesKYBDetails';
import MerchantInvestmentDetails from 'modules/merchantinfo/MerchantInvestmentDetails';
import MerchantPartnerBalancesCard from 'modules/merchantinfo/MerchantPartnerBalancesCard';
import MerchantTransactionsList from 'modules/merchantinfo/MerchantTransactionsList';
import { isOpsDashboard } from 'utils/utils';
import PermissionWrapper from 'components/hoc/PermissionWrapper';

export default function MerchantInfo() {
  let params = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isKycDocsOpen, onOpen: onKycDocsOpen, onClose: onKycDocsClose } = useDisclosure();
  const { isOpen: isInsertTxnOpen, onOpen: onInsertTxnOpen, onClose: onInsertTxnClose } = useDisclosure();
  const { isOpen: isSubmitDocsOpen, onOpen: onSubmitDocsOpen, onClose: onSubmitDocsClose } = useDisclosure();
  const { userPermissions } = useAuthentication();
  const [merchantName, setMerchantName] = useState('');
  const [superMerchantName, setSuperMerchantName] = useState('');
  const [kycDocuments, setKycDocuments] = useState([]);
  const [entities, setEntities] = useState([]);

  const isPaymonade = superMerchantName === 'Paymonade';
  const canEditMerchant = userPermissions?.has(PermissionsV2.EDIT_MERCHANT_INFORMATION);
  const canViewKycDocs = userPermissions?.has(PermissionsV2.VIEW_KYC_DOCUMENTS);
  const canInsertTransaction = userPermissions?.has(PermissionsV2.MANAGE_MERCHANT_TRANSACTION);
  const canSubmitKycDocs = canEditMerchant;
  const showKebabMenu =
    (isPaymonade && (canEditMerchant || canViewKycDocs)) || canInsertTransaction || canSubmitKycDocs;

  useEffect(() => {
    const fetchMerchantDetails = async () => {
      try {
        const response = await getMerchantCompanyDetails(params?.merchantId);
        const data = response?.data?.data;

        setMerchantName(data?.merchant_name || '');
        setSuperMerchantName(data?.super_merchant_name || '');
        setKycDocuments(data?.kyc_documents || []);
        setEntities(data?.entity_details || []);
      } catch {
        // merchant details will remain empty
      }
    };

    if (params?.merchantId) {
      fetchMerchantDetails();
    }
  }, [params?.merchantId]);

  const kebabMenu = showKebabMenu ? (
    <Flex justifyContent='flex-end' mt={4}>
      <Menu>
        <MenuButton
          as={IconButton}
          icon={<BsThreeDotsVertical />}
          variant='ghost'
          aria-label='More options'
          size='sm'
        />
        <MenuList>
          {canEditMerchant && <MenuItem onClick={onOpen}>Change Merchant Name</MenuItem>}
          {canViewKycDocs && <MenuItem onClick={onKycDocsOpen}>Show KYC Documents</MenuItem>}
          {canInsertTransaction && <MenuItem onClick={onInsertTxnOpen}>Insert Transaction</MenuItem>}
          {canSubmitKycDocs && <MenuItem onClick={onSubmitDocsOpen}>Submit Documents</MenuItem>}
        </MenuList>
      </Menu>
    </Flex>
  ) : null;

  if (!isOpsDashboard()) {
    return (
      <Box>
        {kebabMenu}
        <Tabs isFitted variant='enclosed-colored' isLazy>
          <TabList mt={2}>
            <PermissionWrapper permission={PermissionsV2.VIEW_MERCHANT_INFORMATION}>
              <Tab>KYB Details</Tab>
            </PermissionWrapper>
          </TabList>

          <TabPanels>
            <TabPanel>
              <PermissionWrapper permission={PermissionsV2.VIEW_MERCHANT_INFORMATION}>
                <MerchantEntitiesKYBDetails merchantId={params?.merchantId} />
              </PermissionWrapper>
            </TabPanel>
          </TabPanels>
        </Tabs>
        <ChangeMerchantNameModal
          isOpen={isOpen}
          onClose={onClose}
          merchantId={params?.merchantId}
          currentMerchantName={merchantName}
        />
        <KycDocumentsModal isOpen={isKycDocsOpen} onClose={onKycDocsClose} documents={kycDocuments} />
        <InsertTransactionModal isOpen={isInsertTxnOpen} onClose={onInsertTxnClose} merchantId={params?.merchantId} />
        <SubmitKycDocumentModal
          isOpen={isSubmitDocsOpen}
          onClose={onSubmitDocsClose}
          merchantId={params?.merchantId}
          entities={entities}
        />
      </Box>
    );
  }

  return (
    <Box>
      {kebabMenu}
      <Tabs isFitted variant='enclosed-colored' isLazy>
        <TabList mt={2} mb={3}>
          <Tab>Merchant Information</Tab>
          <PermissionWrapper permission={PermissionsV2.VIEW_MERCHANT_CORRIDOR_MANAGEMENT}>
            <Tab>Corridor Management</Tab>
          </PermissionWrapper>
        </TabList>

        <TabPanels>
          <TabPanel>
            <Grid templateColumns={{ sm: 'repeat(1, 1fr)', lg: 'repeat(2, 1fr)' }} gap={6} mb={6}>
              <MerchantAndEntityDetails merchantId={params?.merchantId} />
              <MerchantBankingDetails merchantId={params?.merchantId} />
            </Grid>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(2, 1fr)' }} gap={6} mb={6}>
              <MerchantAccountsDetails merchantId={params?.merchantId} />
              <MerchantPartnerBalancesCard merchantId={params?.merchantId} />
            </Grid>
            <MerchantInvestmentDetails merchantId={params?.merchantId} />
            <MerchantTransactionsList merchantId={params?.merchantId} title={TRANSACTIONS_TITLE} />
          </TabPanel>
          <TabPanel>
            <PermissionWrapper permission={PermissionsV2.VIEW_MERCHANT_CORRIDOR_MANAGEMENT}>
              <MerchantCorridorsManagement merchantId={params?.merchantId} />
            </PermissionWrapper>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <ChangeMerchantNameModal
        isOpen={isOpen}
        onClose={onClose}
        merchantId={params?.merchantId}
        currentMerchantName={merchantName}
      />
      <KycDocumentsModal isOpen={isKycDocsOpen} onClose={onKycDocsClose} documents={kycDocuments} />
      <InsertTransactionModal isOpen={isInsertTxnOpen} onClose={onInsertTxnClose} merchantId={params?.merchantId} />
      <SubmitKycDocumentModal
        isOpen={isSubmitDocsOpen}
        onClose={onSubmitDocsClose}
        merchantId={params?.merchantId}
        entities={entities}
      />
    </Box>
  );
}
